/**
 * brandSanitizer — central whitelabel layer
 *
 * Rewrites third-party LLM / model names into role labels before they reach
 * the user. Vendor-name leaks in toast messages and backend error strings
 * pass through here automatically (see `utils/toast.js`).
 *
 * RULES OF ENGAGEMENT
 *   • KEEP visible: Seedance, Kling, Veo, Runway Aleph (operator-facing
 *     engine choices). Also the internal brand labels (Hyperreal, IGK,
 *     K+, KFP, MJ, GEMINI badges).
 *   • REPLACE: Opus / Claude / GPT / Gemini / Nano Banana / OpenAI /
 *     Anthropic / Midjourney (in copy — not in badges).
 *
 * NEW VENDOR ADDED LATER?
 *   Add one line to MAP and every toast + every backend error auto-renames.
 */

// Order matters — longer / more-specific patterns first so we don't half-match.
const MAP = [
  // ─── Claude / Anthropic family ──────────────────────────────────────────
  [/Claude\s+Opus\s+4\.\d+/gi, 'Director AI'],
  [/Claude\s+Opus/gi, 'Director AI'],
  [/Claude\s+Sonnet\s+\d(?:\.\d)?/gi, 'Director AI'],
  [/Claude\s+Haiku\s+\d(?:\.\d)?/gi, 'Director AI'],
  [/\bOpus\s+4\.\d+/gi, 'Director AI'],
  [/\bOpus\b/g, 'Director AI'],
  [/\bAnthropic\b/gi, 'EiPiCenter'],

  // ─── OpenAI text models → Writer AI ─────────────────────────────────────
  [/GPT[- ]?5\.\d+/gi, 'Writer AI'],
  [/GPT[- ]?4o[- ]?mini/gi, 'Writer AI'],
  [/GPT[- ]?4o/gi, 'Writer AI'],
  [/GPT[- ]?4/gi, 'Writer AI'],

  // ─── Image generation engines ───────────────────────────────────────────
  [/GPT\s+Image\s+[12]/gi, 'Photo engine'],
  [/GPT-Image-[12]/gi, 'Photo engine'],
  [/gpt-image-[12]/gi, 'Photo engine'],

  // ─── Gemini family → Sketch engine ──────────────────────────────────────
  [/Gemini\s+3\s+Pro\s+Image/gi, 'Sketch engine'],
  [/Gemini\s+3\s+Pro/gi, 'Sketch engine'],
  [/Gemini\s+3/gi, 'Sketch engine'],
  [/Nano[- ]?Banana/gi, 'Sketch engine'],
  [/nano[- ]?banana/g, 'Sketch engine'],
  [/NanoBanana/gi, 'Sketch engine'],
  [/\bGemini\b/g, 'Sketch engine'],

  // ─── Misc cleanup ───────────────────────────────────────────────────────
  [/OpenAI[- ]?only\s+pipeline/gi, 'Photo pipeline'],
  [/OpenAI[- ]?only/gi, 'Photo'],
  [/\bOpenAI\b/g, 'EiPiCenter'],
  [/\bMidjourney\b/gi, 'MJ engine'],
  // Cleanup of stutters left behind by the rewrites
  [/EiPiCenter\s+EiPiCenter/g, 'EiPiCenter'],
];

export function sanitizeForUser(msg) {
  if (typeof msg !== 'string' || !msg) return msg;
  let out = msg;
  for (const [re, repl] of MAP) out = out.replace(re, repl);
  return out;
}

// Allow imperative apps to opt-out for, e.g., the admin panel.
export const SANITIZE_DISABLED = (typeof window !== 'undefined') &&
  new URLSearchParams(window.location.search).has('dev');
